import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useOutletContext, useLocation } from "react-router-dom";
import { selectLines } from "app/slice/selectors";
import { NumberSelector } from "./number-selector";
import { CongratsDialog } from "./congrats-dialog";
import { toast } from "app/components/ui/use-toast";
import {
  getMultiNetworkSelectionData,
  saveMultiNetworkSelectionData,
} from "utils/purchase-flow-storage";

import { Button } from "app/components/ui/button";

interface FlowState {
  showAllLines: boolean;
  physicalSimIndices: number[];
  eSimIndices: number[];
}

interface OutletContext {
  selectedLines: number;
  flowState: FlowState | null;
}

type NumberSelection = {
  selectedLabel: number | null;
  mdn: string;
  isValidated: boolean;
};

interface LocationState {
  from?: string;
  getNewLineData?: any;
  multiNetworkPlanId?: string;
}

export const NumberType = () => {
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { selectedLines, flowState } = useOutletContext<OutletContext>();
  const [showCongratsDialog, setShowCongratsDialog] = useState(false);
  const lines = useSelector(selectLines);

  const actualLines = lines || selectedLines;
  const savedData = getMultiNetworkSelectionData();

  const linesToShow = useMemo(() => {
    // Determine which lines to show.
    // If flowState is null (e.g., when navigating back from checkout), show all lines as fallback.
    if (flowState?.showAllLines) {
      return Array.from({ length: actualLines }, (_, i) => i);
    }
    return (
      flowState?.eSimIndices || Array.from({ length: actualLines }, (_, i) => i)
    );
  }, [flowState, actualLines]);

  const [formDataArray, setFormDataArray] = useState<NumberSelection[]>(
    savedData?.numberType && savedData.numberType.length === linesToShow.length
      ? savedData.numberType
      : Array.from({ length: Math.max(linesToShow.length, 0) }, () => ({
          selectedLabel: null,
          mdn: "",
          isValidated: false,
        }))
  );

  // Update formDataArray when linesToShow changes
  useEffect(() => {
    const saved = getMultiNetworkSelectionData();

    if (saved?.numberType && saved.numberType.length === linesToShow.length) {
      setFormDataArray((prev) => {
        const next = saved.numberType!;
        if (
          prev.length === next.length &&
          prev.every(
            (item, i) =>
              item.selectedLabel === next[i]?.selectedLabel &&
              item.mdn === next[i]?.mdn &&
              item.isValidated === next[i]?.isValidated
          )
        ) {
          return prev;
        }
        return next;
      });
    } else {
      setFormDataArray((prev) => {
        if (prev.length === linesToShow.length) return prev;
        return Array.from({ length: Math.max(linesToShow.length, 0) }, () => ({
          selectedLabel: null,
          mdn: "",
          isValidated: false,
        }));
      });
    }
  }, [linesToShow.length]);

  // Save to sessionStorage when formDataArray changes
  useEffect(() => {
    saveMultiNetworkSelectionData({
      numberType: formDataArray,
    });
  }, [formDataArray]);

  const onClickContinue = () => {
    const allFormsFilled = formDataArray.every((form) => {
      if (form.selectedLabel === 0) {
        return true; // "New number" requires no further validation
      }
      if (form.selectedLabel === 1) {
        return form.mdn && form.isValidated;
      }
      return false; // if no selection
    });

    if (allFormsFilled) {
      setShowCongratsDialog(true);
    } else {
      toast({
        title: "Incomplete Information",
        description: "Please complete all the forms before continuing",
        variant: "error",
      });
    }
  };

  // Check if continue button should be shown
  const shouldShowContinueButton = () => {
    return formDataArray.some((form) => {
      if (form.selectedLabel === 0) {
        return true; // Show for "I want a new number"
      }

      if (form.selectedLabel === 1) {
        // Check if validatePorting API response has eligibilityFlag as "true"
        return form.isValidated;
      }

      return false;
    });
  };

  if (linesToShow.length === 0) {
    return null; // No lines to show
  }

  return (
    <React.Fragment>
      <div className="w-full max-w-[31rem] md:mx-auto space-y-4 sm:space-y-5 lg:space-y-6">
        {linesToShow.map((lineIndex, formIndex) => (
          <NumberSelector
            key={lineIndex}
            lineNumber={actualLines !== 1 ? lineIndex + 1 : 0}
            lineFormData={
              formDataArray[formIndex] || {
                selectedLabel: null,
                mdn: "",
                isValidated: false,
              }
            }
            setLineFormData={(updatedData) => {
              setFormDataArray((prev) =>
                prev.map((item, idx) =>
                  idx === formIndex ? updatedData : item
                )
              );
            }}
          />
        ))}
        {shouldShowContinueButton() && (
          <Button type="button" variant="primary" onClick={onClickContinue}>
            Continue
          </Button>
        )}
      </div>
      <CongratsDialog
        showDialog={showCongratsDialog}
        setShowDialog={setShowCongratsDialog}
        locationState={locationState}
      />
    </React.Fragment>
  );
};
